import { useCallback, useMemo } from "react"

import { useAgentsById } from "@/agents/catalog"
import { MAX_TAGS, normalizeTags } from "@/tags/values"

import { useQueryParams } from "@/hooks/useQueryParams"

import { useEvaluationLauncherController } from "./useEvaluationLauncherController"
import {
  collectLaunchResourceTags,
  useLaunchResourceTagsPrefill,
} from "./useLaunchResourceTagsPrefill"

interface UseDirectTemplateLaunchParams {
  organizationId: string
  orgSlug: string
}

export function useDirectTemplateLaunch({
  organizationId,
  orgSlug,
}: UseDirectTemplateLaunchParams) {
  const queryParams = useQueryParams()

  const handleNavigateToRun = useCallback(
    (runIds: string[]) => {
      if (runIds.length === 1) {
        queryParams.push(`/simulated/runs/${runIds[0]}`, orgSlug)
      } else if (runIds.length > 1) {
        queryParams.push("/simulated", orgSlug)
      }
    },
    [orgSlug, queryParams],
  )

  const handleNavigateToReport = useCallback(
    (reportId: string) => {
      queryParams.push(`/reports/${reportId}`, orgSlug)
    },
    [orgSlug, queryParams],
  )

  const controller = useEvaluationLauncherController({
    launchSource: "template_detail",
    onNavigateToReport: handleNavigateToReport,
    onNavigateToRun: handleNavigateToRun,
    organizationId,
  })
  const {
    agentsById,
    isError: agentsError,
    isFetchedAfterMount: agentsFetched,
  } = useAgentsById({ refetchOnMount: "always" })

  const selectedTemplateId =
    controller.templateData.templateId || controller.selectedTemplateId
  const templateTagRecords = useMemo(() => {
    if (!controller.templateData.templateId) {
      return controller.templateRecords
    }

    return [
      controller.templateData,
      ...controller.templateRecords.filter(
        (template) =>
          template.templateId !== controller.templateData.templateId,
      ),
    ]
  }, [controller.templateData, controller.templateRecords])

  const resourceTags = useMemo(
    () =>
      collectLaunchResourceTags({
        agentsById,
        metricRecords: controller.metricRecords,
        personaRecords: controller.personaRecords,
        selectedTemplateId,
        templateData: controller.templateData,
        templateRecords: templateTagRecords,
        testSetRecords: controller.testSetRecords,
      }),
    [
      agentsById,
      controller.metricRecords,
      controller.personaRecords,
      selectedTemplateId,
      controller.templateData,
      controller.testSetRecords,
      templateTagRecords,
    ],
  )
  const expectedTags = useMemo(
    () => normalizeTags(resourceTags, MAX_TAGS),
    [resourceTags],
  )
  const tagsReady =
    expectedTags.length === controller.tags.length &&
    expectedTags.every((tag, index) => tag === controller.tags[index])
  const agentTagsUnavailable =
    agentsError && (controller.templateData.agentIds?.length ?? 0) > 0
  const isPreparing =
    !controller.hasError &&
    !agentTagsUnavailable &&
    (controller.isLoading ||
      !controller.templateData.templateId ||
      !agentsFetched ||
      !tagsReady)

  useLaunchResourceTagsPrefill({
    resourceTags,
    setTags: controller.setTags,
    tags: controller.tags,
  })

  const agentCount = controller.templateData.agentIds?.length ?? 0
  const personaCount = controller.templateData.personaIds?.length ?? 0
  const testSetCount = controller.templateData.testSetIds?.length ?? 0

  return {
    isDisabled:
      !organizationId ||
      Boolean(controller.hasError) ||
      isPreparing ||
      controller.isLaunchDisabled ||
      agentTagsUnavailable,
    isLaunching: controller.isLaunching,
    isPreparing,
    launch: controller.handleLaunch,
    runCount: agentCount * personaCount * testSetCount,
    validationError: controller.hasError
      ? "Saved template details couldn’t be loaded. Close this dialog and try again."
      : agentTagsUnavailable
        ? "Agent tags couldn’t be loaded. Try again."
        : isPreparing
          ? null
          : (controller.validationErrors[0]?.message ?? null),
  }
}
